"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@repo/ui/button";
import { Input } from "@repo/ui/input";
import { SynaptixLogo } from "@repo/ui/logo";
import { Key, Loader2, MapPin, CheckCircle2, AlertTriangle } from "lucide-react";
import { WhatsAppInlineButton, WhatsAppFloat } from "@repo/ui/whatsapp-button";

import Cookies from 'js-cookie';
import dynamic from 'next/dynamic';

const LocationPicker = dynamic(() => import('../../components/LocationPicker'), { ssr: false });

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [address, setAddress] = useState("");
  const [location, setLocation] = useState<{lat: number, lng: number} | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [isRegister, setIsRegister] = useState(false);
  const [step, setStep] = useState<"login" | "map" | "verify">("login");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [resendCount, setResendCount] = useState(0);
  const [coverageStatus, setCoverageStatus] = useState<"none" | "validating" | "available" | "review_required" | "unavailable">("none");
  const [coverageMessage, setCoverageMessage] = useState<string | null>(null);

  const handleCheckCoverage = async () => {
    if (!location && (!address || address.length < 4)) {
      setError("Por favor selecciona tu ubicación en el mapa o escribe tu dirección para validar cobertura.");
      return;
    }
    setError(null);
    setCoverageStatus("validating");

    const lat = location ? location.lat : 4.61357;
    const lng = location ? location.lng : -74.1075055;

    try {
      let res = await fetch(`/api/b2b/availability/check-coverage?lat=${lat}&lng=${lng}`);
      if (!res.ok) {
        res = await fetch(`/api/b2b/availability/check-coverage?lat=${lat}&lng=${lng}`);
      }
      if (res.ok) {
        const data = await res.json();
        if (data.is_covered) {
          setCoverageStatus("available");
          setCoverageMessage("¡Excelente! Tu clínica se encuentra dentro de la zona de Cobertura Activa SYNAPTIX.");
        } else {
          setCoverageStatus("review_required");
          setCoverageMessage("Ubicación fuera de la zona de cobertura automática. Esta zona requiere aprobación manual por nuestro equipo de logística en un máximo de 12 horas. Puedes continuar con la solicitud de registro, pero ten en cuenta que no podrás agendar servicios hasta que se apruebe tu zona.");
        }
        return;
      }
    } catch (err) {
      console.error("Error checking real geofence coverage:", err);
    }

    setCoverageStatus("review_required");
    setCoverageMessage("Ubicación fuera de la zona de cobertura automática. Esta zona requiere aprobación manual por nuestro equipo de logística en un máximo de 12 horas. Puedes continuar con la solicitud de registro, pero ten en cuenta que no podrás agendar servicios hasta que se apruebe tu zona.");
  };

  // Timer countdown logic
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    
    try {
      const response = await fetch("/api/auth/resend-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (response.ok) {
        const nextWaitMinutes = resendCount + 1; // 1 min, then 2 min, etc.
        setResendTimer(nextWaitMinutes * 60);
        setResendCount(resendCount + 1);
        setError("Nuevo código enviado (revisa la consola del backend).");
        setTimeout(() => setError(null), 5000);
      } else {
        setError("Error al reenviar el código.");
      }
    } catch {
      setError("Error de red al reenviar el código.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (step === "verify") {
        const response = await fetch("/api/auth/verify-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, code: otpCode }),
        });
        
        if (response.ok) {
          const data = await response.json();
          Cookies.set("b2b_token", data.access_token, { path: "/" });
          Cookies.remove("token", { path: "/" });
          localStorage.setItem("x-account-id", data.user.id);
          localStorage.setItem("access_token", data.access_token);
          if (data.user.name) localStorage.setItem("clinic_name", data.user.name);
          router.push("/dashboard");
        } else {
          setError("Código de verificación incorrecto.");
        }
      } else if (step === "map") {
        if (!location) {
          setError("Debes seleccionar tu ubicación en el mapa.");
          setLoading(false);
          return;
        }
        
        const endpoint = "/api/auth/register";
        const body = { 
          clinic_name: clinicName, 
          email, 
          password,
          address,
          latitude: location.lat,
          longitude: location.lng
        };
        
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.status === "pending_verification") {
            setStep("verify");
            // Set initial timer for resend (e.g., 60 seconds)
            setResendTimer(60);
            setResendCount(1);
          } else {
            localStorage.setItem("x-account-id", data.user.account_id || data.user.id);
            localStorage.setItem("access_token", data.access_token);
            if (data.user.name) localStorage.setItem("clinic_name", data.user.name);
            router.push("/dashboard");
          }
        } else {
          if (response.status === 409) {
            setError("Este correo ya está registrado. Por favor, inicia sesión.");
            setStep("login");
            setIsRegister(false);
          } else {
            try {
              const errData = await response.json();
              setError(errData.detail || "Error al registrar la cuenta.");
            } catch {
              setError("Error al registrar la cuenta.");
            }
          }
        }
      } else {
        // is login step
        const endpoint = "/api/auth/login";
        const body = { email, password };
        
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.status === "pending_verification") {
            setStep("verify");
            setResendTimer(60);
            setResendCount(1);
          } else {
            Cookies.set("b2b_token", data.access_token, { path: "/" });
            Cookies.remove("token", { path: "/" });
            localStorage.setItem("x-account-id", data.user.account_id || data.user.id);
            localStorage.setItem("access_token", data.access_token);
            if (data.user.name) localStorage.setItem("clinic_name", data.user.name);
            router.push("/dashboard");
          }
        } else {
          if (response.status === 401) {
            setError("Credenciales incorrectas.");
          } else {
            try {
              const errData = await response.json();
              setError(errData.detail || "Error en la solicitud.");
            } catch {
              setError("Credenciales incorrectas.");
            }
          }
        }
      }
    } catch {
      setError("Error de red. Asegúrate de que el backend esté corriendo.");
    } finally {
      setLoading(false);
    }
  };

  const proceedToMap = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!clinicName || !email || !password) {
      setError("Por favor completa todos los campos.");
      return;
    }
    setError(null);
    setStep("map");
  };

  const reverseGeocode = async (lat: number, lng: number, addressFromSearch?: string) => {
    if (addressFromSearch) {
      setAddress(addressFromSearch);
      return;
    }
    if (address && address.length >= 5) {
       return;
    }
    try {
      const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
      if (!MAPBOX_TOKEN) return;
      const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}&types=address,poi`);
      if (res.ok) {
        const data = await res.json();
        if (data.features && data.features.length > 0) {
           const placeName = data.features[0].place_name.split(',').slice(0, 2).join(', ');
           setAddress(placeName);
        }
      }
    } catch (e) {
      console.error("Geocoding failed", e);
    }
  };

  const handleLocationSelect = (lat: number, lng: number, addressFromSearch?: string) => {
    setLocation({ lat, lng });
    setCoverageStatus("none");
    reverseGeocode(lat, lng, addressFromSearch);
  };

  const geocodeAddressInput = async (inputAddress: string) => {
    if (!inputAddress || inputAddress.length < 5) return;
    try {
      const GOOGLE_MAPS_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY;
      if (!GOOGLE_MAPS_KEY) return;
      const query = inputAddress.toLowerCase().includes("colombia") ? inputAddress : `${inputAddress}, Bogota, Colombia`;
      const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=${GOOGLE_MAPS_KEY}`);
      if (res.ok) {
        const data = await res.json();
        if (data.results && data.results.length > 0) {
          const loc = data.results[0].geometry.location;
          setLocation({ lat: loc.lat, lng: loc.lng });
        }
      }
    } catch (e) {
      console.error("Geocoding address failed", e);
    }
  };

  return (
    <div className="min-h-screen bg-synaptix-dark flex flex-col justify-center items-center p-4 font-inter">
      {/* Background Decorators */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-synaptix-navy opacity-30 blur-[100px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-synaptix-cyan opacity-10 blur-[120px]"></div>
      </div>

      <div className={`w-full ${step === "map" ? "max-w-xl" : "max-w-md"} bg-gradient-to-br from-[#0A1424] to-[#0B1D3A] border border-synaptix-cyan/20 p-8 sm:p-10 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.6),0_0_40px_rgba(0,229,255,0.05)] relative z-10 transition-all duration-300`}>
        
        {/* Logo / Header */}
        <div className="flex flex-col items-center mb-8">
          <SynaptixLogo size={70} showText={true} subtitle="B2B Clinic Portal" />
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {error && (
            <div className="p-3 rounded bg-red-500/10 border border-red-500/50 text-red-500 text-sm text-center">
              {error}
            </div>
          )}
          
          {step === "verify" ? (
            <div className="flex flex-col gap-1 animate-in fade-in slide-in-from-right-4">
              <label className="text-xs text-synaptix-titanium uppercase tracking-wider ml-1 text-center mb-2">Ingresa el código OTP enviado a tu correo</label>
              <Input 
                type="text" 
                placeholder="Ej. 123456" 
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                required
                className="text-center tracking-widest text-xl"
                maxLength={6}
              />
              
              <p className="text-[11px] text-slate-400 text-center mt-3 leading-relaxed">
                📩 Revisa tu bandeja de entrada. Si no lo encuentras en unos segundos, por favor <strong className="text-synaptix-cyan font-medium">revisa tu carpeta de SPAM o Correo no deseado</strong>.
              </p>

              <button 
                type="button" 
                onClick={handleResendOTP}
                disabled={resendTimer > 0}
                className="text-xs text-synaptix-cyan mt-2 hover:underline disabled:opacity-50 disabled:no-underline font-medium text-center"
              >
                {resendTimer > 0 
                  ? `Reenviar código en ${Math.floor(resendTimer / 60)}:${(resendTimer % 60).toString().padStart(2, '0')}` 
                  : "¿No recibiste el código? Reenviar"}
              </button>
            </div>
          ) : (
            <>
              {isRegister && step === "login" && (
                <>
                  <div className="flex flex-col gap-1 animate-in fade-in slide-in-from-top-2">
                    <label className="text-xs text-synaptix-titanium uppercase tracking-wider ml-1">Nombre de la Clínica *</label>
                    <Input 
                      type="text" 
                      placeholder="Ej. Clínica Santa María" 
                      value={clinicName}
                      onChange={(e) => setClinicName(e.target.value)}
                      required={isRegister}
                    />
                  </div>

                </>
              )}

              {step === "login" && (
                <>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-synaptix-titanium uppercase tracking-wider ml-1">Correo Electrónico</label>
                    <Input 
                      type="email" 
                      placeholder="admin@synaptix.com" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <div className="flex justify-between items-center ml-1">
                      <label className="text-xs text-synaptix-titanium uppercase tracking-wider">Contraseña</label>
                      <button
                        type="button"
                        onClick={() => router.push("/forgot-password")}
                        className="text-[11px] text-synaptix-cyan hover:text-cyan-300 transition-colors"
                      >
                        ¿Olvidaste tu contraseña?
                      </button>
                    </div>
                    <Input 
                      type="password" 
                      placeholder="••••••••" 
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                </>
              )}
            </>
          )}

          {step === "map" && (
            <div className="flex flex-col gap-4 animate-in fade-in slide-in-from-right-4">
              <div className="bg-synaptix-cyan/10 p-4 rounded-xl border border-synaptix-cyan/20">
                <h3 className="text-synaptix-cyan font-bold mb-1 text-sm">Ubicación de tu Clínica</h3>
                <p className="text-xs text-synaptix-titanium mb-3">Arrastra el pin o busca tu dirección para ubicar las coordenadas exactas de atención.</p>
                <LocationPicker 
                  initialPosition={location ? [location.lat, location.lng] : [4.61357, -74.1075055]}
                  onLocationSelect={handleLocationSelect}
                  className="h-[340px] w-full rounded-lg border border-synaptix-cyan/40 z-0 shadow-lg"
                />
              </div>
              <div className="flex flex-col gap-1 mt-1">
                <label className="text-xs text-synaptix-titanium uppercase tracking-wider ml-1">Dirección Aproximada</label>
                <Input 
                  type="text" 
                  placeholder="Escribe la dirección para mover el mapa..." 
                  value={address}
                  onChange={(e) => {
                    const val = e.target.value;
                    setAddress(val);
                    setCoverageStatus("none");
                  }}
                  onBlur={(e) => geocodeAddressInput(e.target.value)}
                  required
                />
              </div>

              {/* Botón de Validar Cobertura */}
              <div className="flex flex-col gap-2 mt-1">
                <button
                  type="button"
                  onClick={async () => {
                    if (address && address.length >= 5) {
                      await geocodeAddressInput(address);
                    }
                    handleCheckCoverage();
                  }}
                  disabled={coverageStatus === "validating"}
                  className="w-full h-11 px-4 rounded-lg bg-[#0E2443] border border-synaptix-cyan/40 text-synaptix-cyan text-xs font-bold hover:bg-synaptix-cyan/20 transition-all flex items-center justify-center gap-2 shadow-md cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-synaptix-cyan"
                >
                  {coverageStatus === "validating" ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-synaptix-cyan shrink-0" />
                      Validando Cobertura con Torre de Control...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-synaptix-cyan shrink-0" />
                      Validar Cobertura SYNAPTIX
                    </span>
                  )}
                </button>

                {coverageStatus === "available" && (
                  <div className="p-3 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 text-xs font-semibold animate-in fade-in flex items-center gap-2 shadow-[0_0_15px_rgba(16,185,129,0.2)]">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    <span>{coverageMessage}</span>
                  </div>
                )}

                {coverageStatus === "review_required" && (
                  <div className="p-3 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-semibold animate-in fade-in flex items-center gap-2 shadow-[0_0_15px_rgba(245,158,11,0.2)]">
                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                    <span>{coverageMessage}</span>
                  </div>
                )}

                {coverageStatus === "unavailable" && (
                  <div className="p-3 rounded-lg bg-red-500/15 border border-red-500/40 text-red-400 text-xs font-semibold animate-in fade-in flex items-center gap-2">
                    <span>{coverageMessage}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <Button 
            type={isRegister && step === "login" ? "button" : "submit"} 
            onClick={isRegister && step === "login" ? proceedToMap : undefined}
            fullWidth 
            size="lg" 
            disabled={loading} 
            className="mt-4"
          >
            {loading ? "Procesando..." : (step === "verify" ? "Validar Código" : (step === "map" ? "Confirmar Ubicación y Registrar" : (isRegister ? "Siguiente Paso" : "Ingresar")))}
          </Button>

          {step === "login" && (
            <div className="text-center mt-2">
              <button
                type="button"
                onClick={() => {
                  setIsRegister(!isRegister);
                  setError(null);
                  setStep("login");
                }}
                className="text-sm text-synaptix-cyan hover:text-cyan-300 transition-colors"
              >
                {isRegister ? "¿Ya tienes cuenta? Inicia sesión aquí" : "¿No tienes cuenta? Registra tu clínica aquí"}
              </button>
            </div>
          )}
        </form>

        <div className="mt-6 pt-4 border-t border-slate-800/80 flex flex-col items-center">
          <WhatsAppInlineButton
            label="¿Necesitas ayuda o soporte de acceso? Escríbenos por WhatsApp"
            variant="outline"
            className="w-full text-xs py-2.5"
            message="Hola equipo SYNAPTIX, necesito ayuda para iniciar sesión o registrar mi clínica veterinaria."
          />
        </div>
      </div>
      
      <p className="text-xs text-synaptix-titanium mt-8 z-10 text-center">
        &copy; {new Date().getFullYear()} SYNAPTIX HealthTech. Acceso exclusivo autorizado.
      </p>

      <WhatsAppFloat 
        label="¿Ayuda para ingresar?"
        message="Hola equipo SYNAPTIX, necesito ayuda para iniciar sesión o registrar mi clínica veterinaria."
      />
    </div>
  );
}
